import { Suspense, useCallback, useEffect, useMemo } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { PathConstants } from "@/routes/constants";
import {
  Box,
  CircularProgress,
  Container,
  CssBaseline,
  ThemeProvider,
} from "@mui/material";
import useAuth from "@/modules/common/hooks/auth.hook";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import { useAnalytics } from "@/modules/analytics/hooks/analytics.hook";
import { AnalyticsServiceProviders } from "@/modules/analytics/types";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";
import { muiTheme } from "@/styles/theme";
import MobileNavigationSidebar from "@/modules/common/components/layout/side-menu/mobile-side-bar";
import DesktopSideBar from "@/modules/common/components/layout/side-menu/desktop-side-bar";
import NavBar from "@/modules/common/components/layout/side-menu/old-navbar/old-navbar";
import { usePageViewTracking } from "@/modules/analytics/hooks/use-page-view-tracking.hook";
import useWhatsappStatus from "@/modules/common/hooks/use-whatsapp-status.hook";
import WhatsappWarningBanner from "@/modules/common/components/whatsapp-relink/whatsapp-warning-banner";
import WhatsappBlockedOverlay from "@/modules/common/components/whatsapp-relink/whatsapp-blocked-overlay";
import WhatsappSetupPrompt from "@/modules/common/components/whatsapp-relink/whatsapp-setup-prompt";

const DEFAULT_PADDING = "32px";

const paddinglessPages = [
  /^\/leads\/[^/]+$/,
  /^\/agents-tree/,
  /^\/clients$/,
  /^\/clients\/[^/]+$/,
  /^\/property-tracks$/,
  /^\/property-tracks\/[^/]+$/,
  /^\/settings\/property-tracks$/,
  /^\/settings\/property-tracks\/[^/]+$/,
  /^\/leads-new(\/[^/]+)?$/,
  /^\/ambassadors(\/[^/]+)?$/,
  /^\/listing-dashboard(\/.*)?$/,
  /^\/tasks-dashboard(\/.*)?$/,
  /^\/agents-activity(\/.*)?$/,
  /^\/home(\/.*)?$/,
  /^\/nawy-inventory(\/.*)?$/,
  /^\/rental-inventory(\/.*)?$/,
  /^\/auto-delegation-weights(\/.*)?$/,
  /^\/settings\/interests(?:\/.*)?$/,
  /^\/reviews(\/.*)?$/,
  /^\/expo-dashboard(\/.*)?$/,
  /^\/settings$/,
  /^\/zoom-calls(\/.*)?$/,
];

interface LayoutContentProps {
  hasPadding: boolean;
}

const LayoutContent = ({ hasPadding }: LayoutContentProps) => {
  const screen = useScreenSize();

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        flexDirection: "column",
        marginTop: "60px",
        padding: !screen.isMobile && hasPadding ? DEFAULT_PADDING : "0px",
      }}
    >
      <Suspense
        fallback={
          <Container
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100vh",
            }}
          >
            <CircularProgress />
          </Container>
        }
      >
        <Outlet />
      </Suspense>
    </Box>
  );
};

const authPages = [
  PathConstants.LOGIN,
  PathConstants.FRANCHISE_LOGIN,
  PathConstants.SEND_EMAIL_FRANCHISE,
  PathConstants.CHANGE_PASSWORD_FRANCHISE,
  PathConstants.AUTH_CALLBACK,
  PathConstants.AUTH_CHALLENGE,
];

const Layout = () => {
  const { user } = useAuth();
  const { isBlocked, isWarning, needsFirstTimeSetup, gracePeriodMinutes } =
    useWhatsappStatus();

  const location = useLocation();

  const isLeadDetailPage = /^\/leads\/[^/]+$/.test(location.pathname);

  // Auth pages (login, forgot password, …) must render without the app header
  // even when a valid session token exists on the shared cookie domain
  const isAuthPage = authPages.includes(location.pathname);

  const hasPadding = !paddinglessPages.some((page) =>
    page.test(location.pathname),
  );

  const { analytics, ready } = useAnalytics();
  usePageViewTracking();

  useEffect(() => {
    if (analytics && ready) {
      analytics.pageView({
        name: location.pathname,
        services: [AnalyticsServiceProviders.MOENGAGE],
      });

      if (user) {
        analytics.identify(user, [AnalyticsServiceProviders.MOENGAGE]);
      }
    }
    // Note: identification is handled centrally in useAnalytics to avoid repeated/mutable identify calls.
  }, [analytics, ready, location]);

  const theme = useMemo(() => {
    const routesWithNawyTheme = [
      "/settings",
      "/property-tracks",
      "/clients",
      "/targetsv2",
      "/notifications-center",
      "/leads-new",
      "/market-updates",
      "/ambassadors",
      "/leads",
      "/listing-dashboard",
      "/tasks-dashboard",
      "/agents-activity",
      "/auto-delegation-weights",
      "/reviews",
      "/expo-dashboard",
      "/",
    ];

    const routerPath = location.pathname;

    return routesWithNawyTheme.some((route) => routerPath.startsWith(route))
      ? nawyTheme
      : muiTheme;
  }, [location]);

  const renderLayout = useCallback(() => {
    if (isLeadDetailPage) {
      const marginStyle = {
        desktop: isLeadDetailPage ? "0 auto" : "32px",
        tablet: isLeadDetailPage ? "0 auto" : "26px",
      };

      return (
        <Box
          sx={{
            margin: marginStyle,
            maxWidth: "100%",
            overflowX: "hidden",
            display: "flex",
            width: "100%",
          }}
        >
          <LayoutContent hasPadding={hasPadding} />
        </Box>
      );
    }

    return <LayoutContent hasPadding={hasPadding} />;
  }, [hasPadding, isLeadDetailPage]);

  const screen = useScreenSize();
  const sideBarPages = [
    "/",
    "/leads",
    "/ambassadors",
    "/tasks-dashboard",
    "/agents-activity",
    "/listing-dashboard",
    "/home",
    "/auto-delegation-weights",
  ];
  const isRightSideNavbarVisible =
    !screen.isMobile &&
    sideBarPages.includes(location.pathname) &&
    !isLeadDetailPage;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {user && !isAuthPage && (
        <>
          {screen.isTablet || screen.isMobile ? (
            <MobileNavigationSidebar />
          ) : (
            <NavBar />
          )}
          <Box>
            {isRightSideNavbarVisible && (
              <Box sx={{ display: "flex", maxWidth: "60px" }}>
                <DesktopSideBar />
              </Box>
            )}
          </Box>
        </>
      )}

      <Box
        sx={{
          display: "flex",
          width: "100%",
          height: "100%",
          paddingRight: isRightSideNavbarVisible ? "60px" : 0,
        }}
      >
        {renderLayout()}
      </Box>

      {user && isWarning && (
        <WhatsappWarningBanner gracePeriodMinutes={gracePeriodMinutes} />
      )}
      {user && isBlocked && <WhatsappBlockedOverlay />}
      {user && needsFirstTimeSetup && <WhatsappSetupPrompt />}
    </ThemeProvider>
  );
};

export default Layout;
